// Focus / Pipeline 用のデータ集約フック（DEC-054）。
//
// ユーザーが参加する全プロジェクトを購読し、各プロジェクトの issues / meetings /
// deal を読み取って Focus（約束）と Pipeline（案件）を組み立てる。Web 版は「読むだけ」。
//
// 設計メモ:
//   - projects は onSnapshot でリアルタイム購読（プロジェクト増減に追従）。
//   - 各プロジェクトの issues も onSnapshot 購読（[果たした] 等の書込みを即時反映）。
//   - meetings は recordedAt と companyIds の解決にだけ使うので getDocs（一括取得）。
//     会社マスタ（useCompanies）で companyId → 社名を解決する。
//   - 集約ロジックは focus.ts の純関数に委譲（このフックは購読配線に専念）。
import { useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { useCompanies, UNCLASSIFIED_ID } from './useCompanies';
import {
  buildFocusPromise, FocusPromise, ProjectCtx, RawIssue, toDate, isUnresolved,
} from './focus';

interface RawProject {
  id: string;
  name?: string;
  deal?: any;
  updatedAt?: any;
}

// プロジェクトごとの会議メタ（社名解決＋約束の会議日解決に使う）。
interface MeetingMeta {
  dominantCompanyId: string | null;
  meetingDates: Record<string, Date | null>;
  meetingTitles: Record<string, string>;
}

export interface FocusData {
  loading: boolean;
  error: string | null;
  promises: FocusPromise[]; // 未解決の全約束（audience 問わず）
  promisesByProject: Record<string, FocusPromise[]>;
  projects: ProjectCtx[];
}

// 会議群から最頻の companyId を求める（相手先の主担当）。
function dominantCompany(docs: { companyIds?: string[] }[]): string | null {
  const tally: Record<string, number> = {};
  docs.forEach((m) => {
    const ids = Array.isArray(m.companyIds) && m.companyIds.length > 0 ? m.companyIds : [];
    ids.forEach((cid) => { tally[cid] = (tally[cid] || 0) + 1; });
  });
  let best: string | null = null;
  let bestN = 0;
  Object.entries(tally).forEach(([cid, n]) => {
    if (cid === UNCLASSIFIED_ID) return;
    if (n > bestN) { best = cid; bestN = n; }
  });
  return best;
}

export function useFocusData(uid: string | undefined): FocusData {
  const [projects, setProjects] = useState<RawProject[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // projectId -> 未解決 issue 群（onSnapshot 由来）
  const [issuesByProject, setIssuesByProject] = useState<Record<string, RawIssue[]>>({});
  // projectId -> 会議メタ（getDocs 由来）
  const [meetingMeta, setMeetingMeta] = useState<Record<string, MeetingMeta>>({});

  const { companyMap } = useCompanies(uid);

  // ── 参加プロジェクト購読 ──
  useEffect(() => {
    if (!uid) {
      setProjects([]);
      setProjectsLoading(false);
      return;
    }
    setProjectsLoading(true);
    const qref = query(collection(db, 'projects'), where('members', 'array-contains', uid));
    const unsub = onSnapshot(
      qref,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) } as RawProject));
        setProjects(data);
        setError(null);
        setProjectsLoading(false);
      },
      (e: any) => {
        console.error('[useFocusData] failed to subscribe projects:', e);
        const detail = e?.code ? `${e.code}: ${e.message ?? e}` : (e?.message ?? String(e));
        setError(`案件の取得に失敗しました (${detail})`);
        setProjectsLoading(false);
      }
    );
    return unsub;
  }, [uid]);

  const projectIds = useMemo(() => projects.map((p) => p.id).sort().join('|'), [projects]);

  // ── 各プロジェクトの issues 購読（projectIds が変わったら張り直す） ──
  // 個々の unsubscribe を保持し、集合が変わったタイミングでまとめて解除する。
  const issueUnsubsRef = useRef<Record<string, () => void>>({});
  useEffect(() => {
    const ids = projects.map((p) => p.id);
    const subs = issueUnsubsRef.current;

    // 新規プロジェクト分を購読。
    ids.forEach((pid) => {
      if (subs[pid]) return;
      subs[pid] = onSnapshot(
        collection(db, 'projects', pid, 'issues'),
        (snap) => {
          const open = snap.docs
            .map((d) => ({ id: d.id, projectId: pid, ...(d.data() as any) } as RawIssue))
            .filter((i) => isUnresolved(i));
          setIssuesByProject((prev) => ({ ...prev, [pid]: open }));
        },
        (e: any) => {
          console.error('[useFocusData] failed to subscribe issues:', pid, e);
        }
      );
    });

    // 抜けたプロジェクト分を解除＆破棄。
    Object.keys(subs).forEach((pid) => {
      if (!ids.includes(pid)) {
        subs[pid]();
        delete subs[pid];
        setIssuesByProject((prev) => { const n = { ...prev }; delete n[pid]; return n; });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectIds]);

  // アンマウント時に全 issue 購読を解除。
  useEffect(() => () => {
    Object.values(issueUnsubsRef.current).forEach((u) => u());
    issueUnsubsRef.current = {};
  }, []);

  // ── 各プロジェクトの会議メタ取得（一括 getDocs・社名と会議日の解決用） ──
  useEffect(() => {
    if (projects.length === 0) return;
    let cancelled = false;
    (async () => {
      const result: Record<string, MeetingMeta> = {};
      for (const p of projects) {
        try {
          const snap = await getDocs(collection(db, 'projects', p.id, 'meetings'));
          const docs = snap.docs.map((d) => d.data() as any).filter((m) => m.deletedAt == null);
          const meetingDates: Record<string, Date | null> = {};
          const meetingTitles: Record<string, string> = {};
          snap.docs.forEach((d) => {
            const m = d.data() as any;
            if (m.deletedAt != null) return;
            meetingDates[d.id] = toDate(m.recordedAt);
            if (typeof m.title === 'string' && m.title) meetingTitles[d.id] = m.title;
          });
          result[p.id] = { dominantCompanyId: dominantCompany(docs), meetingDates, meetingTitles };
        } catch (e) {
          console.error('[useFocusData] failed to load meetings:', p.id, e);
          result[p.id] = { dominantCompanyId: null, meetingDates: {}, meetingTitles: {} };
        }
      }
      if (!cancelled) setMeetingMeta(result);
    })();
    return () => { cancelled = true; };
  }, [projectIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 集約 ──
  const { promises, promisesByProject, projectCtxs } = useMemo(() => {
    const now = new Date();
    const ctxs: ProjectCtx[] = projects.map((p) => {
      const meta = meetingMeta[p.id];
      const companyName = meta?.dominantCompanyId
        ? companyMap[meta.dominantCompanyId]?.name
        : undefined;
      const deal = p.deal && typeof p.deal === 'object' ? {
        ball: p.deal.ball,
        hot: p.deal.hot,
        hotEvidence: p.deal.hotEvidence,
        confidenceBand: p.deal.confidenceBand,
        customerIssue: p.deal.customerIssue,
      } : undefined;
      return {
        id: p.id,
        name: p.name || '無題の案件',
        companyName,
        deal,
        meetingDates: meta?.meetingDates,
        meetingTitles: meta?.meetingTitles,
      };
    });
    const ctxMap = Object.fromEntries(ctxs.map((c) => [c.id, c]));

    const byProject: Record<string, FocusPromise[]> = {};
    const all: FocusPromise[] = [];
    Object.entries(issuesByProject).forEach(([pid, issues]) => {
      const ctx = ctxMap[pid];
      if (!ctx) return;
      const built = issues.map((i) => buildFocusPromise(i, ctx, now));
      byProject[pid] = built;
      all.push(...built);
    });

    return { promises: all, promisesByProject: byProject, projectCtxs: ctxs };
  }, [projects, issuesByProject, meetingMeta, companyMap]);

  return {
    loading: projectsLoading,
    error,
    promises,
    promisesByProject,
    projects: projectCtxs,
  };
}
